import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  addDays,
  addMonths,
  addYears,
  endOfWeek,
  format,
  isValid,
  parse,
  setMonth,
  setYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { cn } from '../lib/cn';
import { controlClasses, useFieldContext } from './field-context';

/**
 * The date picker.
 *
 * `<input type="date">` renders whatever calendar the browser feels like —
 * Chrome's is a different shape, a different palette and a different keyboard
 * model to everything else in this application, and it cannot be styled. This
 * is the same field built from the design tokens, so a date field is a sibling
 * of `Select` rather than a visitor from the browser chrome.
 *
 * The value is always an ISO `yyyy-MM-dd` string — the shape the services and
 * the API speak — while the field *displays* `dd MMM yyyy`, matching
 * `formatDate` in the web app. The two never mix: parsing happens once, on
 * commit, and a string that cannot be parsed is refused rather than guessed at.
 */

/** The wire format. Every `value`, `min`, `max` and `onChange` argument. */
const ISO_FORMAT = 'yyyy-MM-dd';

/** The reading format, matching `formatDate` in the web app. */
const DISPLAY_FORMAT = 'dd MMM yyyy';

/**
 * Formats accepted from the keyboard, most specific first.
 *
 * Numeric slash dates are read month-first because that is the order Philippine
 * offices write and read them; a day-first reading is tried afterwards so
 * `13/08/2026` still lands on 13 August rather than being refused.
 */
const PARSE_FORMATS = [
  ISO_FORMAT,
  'yyyy/MM/dd',
  'MM/dd/yyyy',
  'M/d/yyyy',
  'dd/MM/yyyy',
  'd/M/yyyy',
  'MM-dd-yyyy',
  'M-d-yyyy',
  'dd.MM.yyyy',
  'd.M.yyyy',
  DISPLAY_FORMAT,
  'd MMM yyyy',
  'd MMMM yyyy',
  'dd MMMM yyyy',
  'MMM d yyyy',
  'MMM d, yyyy',
  'MMMM d yyyy',
  'MMMM d, yyyy',
  'yyyyMMdd',
] as const;

/** Fixed so a pattern that omits a field cannot pick up today's value for it. */
const PARSE_REFERENCE = new Date(2000, 0, 1);

/** Parses an ISO `yyyy-MM-dd` string, returning `null` for anything else. */
export function parseIsoDate(value: string | undefined | null): Date | null {
  if (!value) return null;
  const parsed = parse(value, ISO_FORMAT, PARSE_REFERENCE);
  return isValid(parsed) ? parsed : null;
}

export function toIsoDate(date: Date): string {
  return format(date, ISO_FORMAT);
}

/**
 * Reads a hand-typed date, or returns `null`.
 *
 * `date-fns` rolls impossible dates forward — 31 February becomes 3 March — so
 * a parse only counts once it survives a round trip back through the same
 * pattern. Without that check the field would silently record a date the user
 * never typed, which is worse than refusing the input.
 */
export function parseDateInput(text: string): string | null {
  const trimmed = text.trim().replace(/\s+/g, ' ');
  if (!trimmed) return null;
  for (const pattern of PARSE_FORMATS) {
    const parsed = parse(trimmed, pattern, PARSE_REFERENCE);
    if (!isValid(parsed)) continue;
    if (format(parsed, pattern).toLowerCase() !== trimmed.toLowerCase()) continue;
    return toIsoDate(parsed);
  }
  return null;
}

export function formatDisplayDate(value: string | undefined): string {
  const date = parseIsoDate(value);
  // An unparseable value is shown as-is rather than blanked: silently emptying
  // a field is how data goes missing without anyone noticing.
  return date ? format(date, DISPLAY_FORMAT) : (value ?? '');
}

export interface DatePickerProps {
  /** ISO `yyyy-MM-dd`, or empty. */
  value?: string;
  onChange: (value: string) => void;
  /** Fired when the field loses focus — `Controller`'s `field.onBlur`. */
  onBlur?: () => void;
  /** ISO bounds. Days outside them are rendered disabled, not merely rejected. */
  min?: string;
  max?: string;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  /** Emitted on a hidden input so a native form submit carries the ISO value. */
  name?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** 0 = Sunday, matching the week the rest of the product starts on. */
  weekStartsOn?: 0 | 1;
}

type CalendarView = 'days' | 'months' | 'years';

const YEARS_PER_PAGE = 12;

const dayCellClasses =
  'flex h-8 w-8 items-center justify-center rounded-md text-base tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40';

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  {
    value,
    onChange,
    onBlur,
    min,
    max,
    disabled,
    placeholder = 'dd mmm yyyy',
    id,
    name,
    className,
    weekStartsOn = 0,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  },
  ref,
) {
  const field = useFieldContext();
  const generatedId = useId();
  const inputId = field.id || id || generatedId;
  const errorId = `${generatedId}-parse-error`;
  const captionId = `${generatedId}-caption`;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const focusedDayRef = useRef<HTMLButtonElement | null>(null);
  /** Set only by keyboard navigation, so a mouse click never yanks focus. */
  const moveFocusToDay = useRef(false);

  const selected = parseIsoDate(value);
  const minDate = parseIsoDate(min);
  const maxDate = parseIsoDate(max);
  const today = useMemo(() => startOfDay(new Date()), []);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<CalendarView>('days');
  /** Non-null only while the user is typing; otherwise the value is displayed. */
  const [draft, setDraft] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(selected ?? today));
  const [focusedDay, setFocusedDay] = useState(() => selected ?? today);

  // The panel opens on whatever the field currently holds, not on wherever it
  // was left the last time it was open.
  useEffect(() => {
    if (!open) return;
    const anchor = parseIsoDate(value) ?? today;
    setVisibleMonth(startOfMonth(anchor));
    setFocusedDay(anchor);
    setView('days');
    moveFocusToDay.current = true;
  }, [open, value, today]);

  /**
   * Focus follows the grid from a callback ref rather than an effect, because
   * Radix mounts the portal on a render of its own — by the time an effect in
   * *this* component could look for the day button, it does not exist yet, and
   * the render that creates it never re-runs the effect.
   */
  const attachFocusedDay = useCallback((day: HTMLButtonElement | null) => {
    focusedDayRef.current = day;
    if (!day || !moveFocusToDay.current) return;
    moveFocusToDay.current = false;
    day.focus();
  }, []);

  const isOutOfRange = (date: Date): boolean => {
    if (minDate && date < startOfDay(minDate)) return true;
    if (maxDate && date > startOfDay(maxDate)) return true;
    return false;
  };

  const clampToRange = (date: Date): Date => {
    if (minDate && date < minDate) return minDate;
    if (maxDate && date > maxDate) return maxDate;
    return date;
  };

  const weeks = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(visibleMonth), { weekStartsOn });
    const rows: Date[][] = [];
    let cursor = gridStart;
    // Six rows always: a grid that changes height between months makes the
    // whole popover jump as you page through it.
    for (let week = 0; week < 6; week += 1) {
      const days: Date[] = [];
      for (let day = 0; day < 7; day += 1) {
        days.push(cursor);
        cursor = addDays(cursor, 1);
      }
      rows.push(days);
    }
    return rows;
  }, [visibleMonth, weekStartsOn]);

  const weekdays = useMemo(() => {
    const start = startOfWeek(new Date(), { weekStartsOn });
    return Array.from({ length: 7 }, (_, index) => addDays(start, index));
  }, [weekStartsOn]);

  const commitSelection = (date: Date): void => {
    onChange(toIsoDate(date));
    setDraft(null);
    setParseError(null);
    setOpen(false);
  };

  const commitDraft = (): void => {
    if (draft === null) return;
    const text = draft.trim();
    setDraft(null);
    if (!text) {
      setParseError(null);
      if (value) onChange('');
      return;
    }
    const iso = parseDateInput(text);
    if (!iso) {
      setParseError(`“${text}” is not a date. Try 08 Aug 2026, 08/08/2026 or 2026-08-08.`);
      return;
    }
    const parsed = parseIsoDate(iso);
    if (parsed && isOutOfRange(parsed)) {
      const bound =
        minDate && parsed < minDate
          ? `on or after ${format(minDate, DISPLAY_FORMAT)}`
          : `on or before ${format(maxDate as Date, DISPLAY_FORMAT)}`;
      setParseError(`The date must be ${bound}.`);
      return;
    }
    setParseError(null);
    onChange(iso);
  };

  const moveFocus = (next: Date): void => {
    const target = clampToRange(next);
    setFocusedDay(target);
    setVisibleMonth(startOfMonth(target));
    moveFocusToDay.current = true;
  };

  const onGridKeyDown = (event: ReactKeyboardEvent<HTMLTableElement>): void => {
    const stride: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    if (event.key in stride) {
      event.preventDefault();
      moveFocus(addDays(focusedDay, stride[event.key] as number));
      return;
    }
    switch (event.key) {
      case 'PageUp':
        event.preventDefault();
        moveFocus(event.shiftKey ? addYears(focusedDay, -1) : addMonths(focusedDay, -1));
        return;
      case 'PageDown':
        event.preventDefault();
        moveFocus(event.shiftKey ? addYears(focusedDay, 1) : addMonths(focusedDay, 1));
        return;
      case 'Home':
        event.preventDefault();
        moveFocus(startOfWeek(focusedDay, { weekStartsOn }));
        return;
      case 'End':
        event.preventDefault();
        moveFocus(endOfWeek(focusedDay, { weekStartsOn }));
        return;
      default:
    }
  };

  const displayValue = draft ?? formatDisplayDate(value);
  const describedBy =
    [field.describedBy, parseError ? errorId : null].filter(Boolean).join(' ') || undefined;
  const invalid = field.invalid || Boolean(parseError);
  const yearPageStart = Math.floor(visibleMonth.getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE;

  return (
    <div className={cn('min-w-0', className)}>
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <div className="relative">
            <input
              ref={(node) => {
                inputRef.current = node;
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
              }}
              id={inputId}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              disabled={disabled}
              placeholder={placeholder}
              value={displayValue}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              onChange={(event) => {
                const text = event.target.value;
                setDraft(text);
                /*
                 * A complete, valid date is reported as it is typed, not only
                 * when the field loses focus. Waiting for blur left a form's
                 * submit button disabled beside a perfectly good date until
                 * the user clicked somewhere else first. Text that does not
                 * parse yet changes nothing: a half-typed date is not a
                 * mistake, and on commit an unreadable one reverts to the last
                 * good value with its error, exactly as before.
                 */
                const iso = parseDateInput(text);
                const parsed = iso ? parseIsoDate(iso) : null;
                if (iso && parsed && !isOutOfRange(parsed)) {
                  if (iso !== value) onChange(iso);
                  setParseError(null);
                }
              }}
              onBlur={() => {
                commitDraft();
                onBlur?.();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  // Committing must not also submit the form the field sits in.
                  event.preventDefault();
                  commitDraft();
                } else if (event.key === 'ArrowDown' && !open) {
                  event.preventDefault();
                  setOpen(true);
                } else if (event.key === 'Escape' && draft !== null) {
                  event.preventDefault();
                  setDraft(null);
                  setParseError(null);
                }
              }}
              className={cn(
                controlClasses,
                'h-9 pr-9',
                invalid
                  ? 'border-danger focus:ring-danger/25'
                  : 'border-line-strong focus:border-brand',
              )}
            />
            <PopoverPrimitive.Trigger asChild>
              <button
                type="button"
                disabled={disabled}
                aria-label="Choose date from calendar"
                className={cn(
                  'absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-r-md text-ink-muted transition-colors',
                  'hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
                  'disabled:cursor-not-allowed disabled:text-ink-subtle',
                )}
              >
                <CalendarIcon className="h-4 w-4" aria-hidden />
              </button>
            </PopoverPrimitive.Trigger>
          </div>
        </PopoverPrimitive.Anchor>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={4}
            aria-label="Calendar"
            // Reduced motion is a stated preference, not a hint: the panel
            // still appears, it simply stops fading in.
            className="z-50 w-[17.5rem] animate-fade-in rounded-lg border border-line bg-surface p-3 shadow-lg focus:outline-none motion-reduce:animate-none"
            onOpenAutoFocus={(event) => {
              // Focus belongs on the focused day, which the effect above moves
              // it to once the grid has rendered.
              event.preventDefault();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              inputRef.current?.focus();
            }}
          >
            <div className="mb-2 flex items-center gap-1">
              <button
                type="button"
                aria-label="Previous year"
                onClick={() => setVisibleMonth(addYears(visibleMonth, -1))}
                className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-neutral-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                <ChevronsLeft className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                aria-label="Previous month"
                onClick={() => setVisibleMonth(addMonths(visibleMonth, -1))}
                className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-neutral-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                id={captionId}
                aria-label={`${format(visibleMonth, 'MMMM yyyy')}, choose a different month or year`}
                onClick={() => setView(view === 'days' ? 'months' : 'days')}
                className="flex-1 rounded-md px-2 py-1 text-base font-medium text-ink transition-colors hover:bg-neutral-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                {format(visibleMonth, 'MMMM yyyy')}
              </button>
              <button
                type="button"
                aria-label="Next month"
                onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))}
                className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-neutral-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                aria-label="Next year"
                onClick={() => setVisibleMonth(addYears(visibleMonth, 1))}
                className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-neutral-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                <ChevronsRight className="h-4 w-4" aria-hidden />
              </button>
            </div>

            {/* Screen readers are told where the grid has moved to; sighted
                users read the caption above. */}
            <div aria-live="polite" className="sr-only">
              {format(visibleMonth, 'MMMM yyyy')}
            </div>

            {view === 'days' ? (
              <table
                role="grid"
                aria-labelledby={captionId}
                onKeyDown={onGridKeyDown}
                className="w-full border-collapse"
              >
                <thead>
                  <tr>
                    {weekdays.map((day) => (
                      <th
                        key={day.toISOString()}
                        scope="col"
                        className="pb-1 text-center text-caption font-medium text-ink-subtle"
                      >
                        <span aria-hidden>{format(day, 'EEEEE')}</span>
                        <span className="sr-only">{format(day, 'EEEE')}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {weeks.map((week) => (
                    <tr key={week[0]?.toISOString()}>
                      {week.map((day) => {
                        const iso = toIsoDate(day);
                        const isSelected = Boolean(selected) && iso === toIsoDate(selected as Date);
                        const isToday = iso === toIsoDate(today);
                        const isOutside = day.getMonth() !== visibleMonth.getMonth();
                        const isDisabled = isOutOfRange(day);
                        const isFocused = iso === toIsoDate(focusedDay);
                        return (
                          <td
                            key={iso}
                            role="gridcell"
                            aria-selected={isSelected}
                            className="p-0 text-center"
                          >
                            <button
                              ref={isFocused ? attachFocusedDay : undefined}
                              type="button"
                              tabIndex={isFocused ? 0 : -1}
                              disabled={isDisabled}
                              aria-label={format(day, 'd MMMM yyyy')}
                              aria-current={isToday ? 'date' : undefined}
                              onClick={() => commitSelection(day)}
                              className={cn(
                                dayCellClasses,
                                'mx-auto',
                                isSelected
                                  ? 'bg-brand font-medium text-white hover:bg-brand-hover'
                                  : isToday
                                    ? 'border border-brand font-medium text-brand hover:bg-brand-soft'
                                    : isOutside
                                      ? 'text-ink-subtle hover:bg-neutral-soft'
                                      : 'text-ink hover:bg-neutral-soft',
                                isDisabled &&
                                  'cursor-not-allowed text-ink-subtle opacity-40 hover:bg-transparent',
                              )}
                            >
                              {format(day, 'd')}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}

            {view === 'months' ? (
              <div className="grid grid-cols-3 gap-1" aria-label="Choose month">
                {Array.from({ length: 12 }, (_, month) => (
                  <button
                    key={month}
                    type="button"
                    onClick={() => {
                      setVisibleMonth(setMonth(visibleMonth, month));
                      setView('days');
                    }}
                    className={cn(
                      'rounded-md px-2 py-2 text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
                      month === visibleMonth.getMonth()
                        ? 'bg-brand font-medium text-white hover:bg-brand-hover'
                        : 'text-ink hover:bg-neutral-soft',
                    )}
                  >
                    {format(setMonth(visibleMonth, month), 'MMM')}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setView('years')}
                  className="col-span-3 mt-1 rounded-md px-2 py-1.5 text-base text-ink-muted transition-colors hover:bg-neutral-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                >
                  Choose a year
                </button>
              </div>
            ) : null}

            {view === 'years' ? (
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <button
                    type="button"
                    aria-label="Earlier years"
                    onClick={() => setVisibleMonth(addYears(visibleMonth, -YEARS_PER_PAGE))}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-neutral-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden />
                  </button>
                  <span className="text-caption text-ink-muted tabular-nums">
                    {yearPageStart}–{yearPageStart + YEARS_PER_PAGE - 1}
                  </span>
                  <button
                    type="button"
                    aria-label="Later years"
                    onClick={() => setVisibleMonth(addYears(visibleMonth, YEARS_PER_PAGE))}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-neutral-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {Array.from({ length: YEARS_PER_PAGE }, (_, index) => yearPageStart + index).map(
                    (year) => (
                      <button
                        key={year}
                        type="button"
                        onClick={() => {
                          setVisibleMonth(setYear(visibleMonth, year));
                          setView('months');
                        }}
                        className={cn(
                          'rounded-md px-2 py-2 text-base tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
                          year === visibleMonth.getFullYear()
                            ? 'bg-brand font-medium text-white hover:bg-brand-hover'
                            : 'text-ink hover:bg-neutral-soft',
                        )}
                      >
                        {year}
                      </button>
                    ),
                  )}
                </div>
              </div>
            ) : null}

            <div className="mt-2 flex items-center justify-between border-t border-line pt-2">
              <button
                type="button"
                disabled={isOutOfRange(today)}
                onClick={() => commitSelection(today)}
                className="rounded-md px-2 py-1 text-caption font-medium text-brand transition-colors hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed disabled:text-ink-subtle disabled:hover:bg-transparent"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setDraft(null);
                  setParseError(null);
                  setOpen(false);
                }}
                className="rounded-md px-2 py-1 text-caption text-ink-muted transition-colors hover:bg-neutral-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                Clear
              </button>
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      {name ? <input type="hidden" name={name} value={value ?? ''} /> : null}

      {parseError ? (
        <p id={errorId} role="alert" className="mt-1.5 text-sm text-danger">
          {parseError}
        </p>
      ) : null}
    </div>
  );
});
